import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductDetail } from "../redux/actions/productDetailsActions";
import {
  fetchWishlist,
  toggleWishlistItem,
} from "../redux/actions/wishlistAction";
import { IoStar } from "react-icons/io5";
import { TfiCommentAlt } from "react-icons/tfi";
import { IoIosArrowForward } from "react-icons/io";
import { FaCartPlus } from "react-icons/fa6";
import * as other from "../components/other";
import { addToCartThunk } from "../redux/actions/cartActions";
import WishlistButton from "../components/other/WishlistButton";
import { IoHeartOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { checkStockAvailability } from "../redux/actions/stockActions";
import * as Alert from "../components/common/Alert";
import { useProductDetails } from "../hooks/product";

const ProductDetail = () => {
  const { pro_id } = useParams();
  const dispatch = useDispatch();
  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const {
    loading,
    productDetails: product,
    error,
    refetch,
  } = useProductDetails();
  const userType = useSelector((state) => state.auth?.user?.user?.user_type);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const {
    stockData,
    loading: stockLoading,
    error: stockError,
  } = useSelector((state) => state.stock);

  // product here is the full API response; product.data is the actual product info
  const productData = product?.data;
  const [mainImage, setMainImage] = useState("");
  const [otherImages, setOtherImages] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subProducts, setSubProducts] = useState([]);
  const navigate = useNavigate();
  const [quantities, setQuantities] = useState({});

  useEffect(() => {
    if (product?.data) {
      setMainImage(product.data.product_primary_img);
      setOtherImages(product.data.other_images || []);
    }
  }, [product]);

  useEffect(() => {
    refetch(pro_id);
  }, [pro_id]);

  useEffect(() => {
    dispatch(fetchWishlist());
  }, [dispatch]);

  const handleImageSwap = (clickedImg, index) => {
    const newOtherImages = [...otherImages];
    newOtherImages[index] = { product_img: mainImage };
    setMainImage(clickedImg);
    setOtherImages(newOtherImages);
  };

  const handleAddToCartClick = () => {
    if (product?.data?.SubProducts?.length) {
      setSubProducts(product.data.SubProducts);
      setIsModalOpen(true);
    }
  };

  const handleAddSubProductToCart = (sub) => {
    const product_id = productData.pro_id;
    const sub_product_id = sub.sub_pro_id;
    const quantity = quantities[sub_product_id] || 1;

    dispatch(checkStockAvailability(sub_product_id, quantity))
      .then((response) => {
        // Log the entire response to see its structure

        // The response structure is likely different than expected
        // The data might be directly in response.data, not in response.payload.data
        const data = response.data || {};

        // Check if there's stock available
        if (
          data.data?.status === "out_of_stock" ||
          data.data?.available_stock < data.data?.required_quantity
        ) {
          Alert.Alert.ErrorAlert("Insufficient stock!");
        } else {
          // Stock is available, increment quantity
          dispatch(addToCartThunk({ product_id, sub_product_id, quantity }));
        }
      })
      .catch((error) => {
        // Log the error for debugging

        // Display a more specific error message if available
        const errorMessage =
          error.message || "Failed to check stock. Please try again.";
        toast.error(errorMessage);
      });
  };

  // Quantity controls handlers
  const incrementQuantity = (sub) => {
    const sub_product_id = sub.sub_pro_id;
    const quantity = (quantities[sub_product_id] || 1) + 1;

    // First, log what we're sending

    dispatch(checkStockAvailability(sub_product_id, quantity))
      .then((response) => {
        // Log the entire response to see its structure

        // The response structure is likely different than expected
        // The data might be directly in response.data, not in response.payload.data
        const data = response.data || {};

        // Check if there's stock available
        if (
          data.data?.status === "out_of_stock" ||
          data.data?.available_stock < data.data?.required_quantity
        ) {
          toast.info("Insufficient Stock !!!");
        } else {
          // Stock is available, increment quantity
          setQuantities((prev) => ({
            ...prev,
            [sub_product_id]: quantity,
          }));
        }
      })
      .catch((error) => {
        // Log the error for debugging

        // Display a more specific error message if available
        const errorMessage =
          error.message || "Failed to check stock. Please try again.";
        toast.error(errorMessage);
      });
  };

  const decrementQuantity = (id) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: prev[id] > 1 ? prev[id] - 1 : 1,
    }));
  };

  const isWishlisted = productData
    ? wishlistItems.some(
        (item) => (item.product?.pro_id || item.pro_id) === productData.pro_id
      )
    : false;

  if (loading)
    return <div className="text-center my-5">Loading product details...</div>;
  if (error)
    return <div className="text-center text-red-500 my-5">Error: {error}</div>;
  if (!productData)
    return <div className="text-center my-5">No product found.</div>;

  return (
    <div className="container-fluid my-2 min-h-[60vh] max-w-[1600px] mx-auto">
      <p className="flex items-center gap-2 text-gray-600">
        <span>Shop</span>
        <IoIosArrowForward />
        <span>{productData.sub_cat_name || productData.product_category}</span>
        <IoIosArrowForward />
        <span>{productData.tag_name || productData.product_type}</span>
      </p>
      <div className="flex flex-col gap-3 lg:flex-row">
        <div className="flex flex-col lg:flex-row lg:max-w-[50%] gap-2 p-4">
          <div className="flex flex-col-reverse gap-4 items-center lg:flex-row">
            <div className="flex lg:flex-col gap-2 justify-center items-center lg:mr-4">
              {otherImages.slice(0, 3).map((imgObj, idx) => (
                <div
                  className={`w-1/4 md:w-36 aspect-[3/4] overflow-hidden hover:scale-110 transition-transform duration-300 ease-in-out ${
                    mainImage === imgObj.product_img
                      ? "border-red-500 shadow-md"
                      : "border-transparent"
                  }}`}
                  key={idx}
                  onClick={() => handleImageSwap(imgObj.product_img, idx)}
                >
                  <img
                    loading="lazy"
                    key={idx}
                    src={imgObj.product_img}
                    alt={`product thumb ${idx + 1}`}
                  />
                </div>
              ))}
            </div>

            <div className="aspect-[3/4]">
              <img
                loading="lazy"
                src={mainImage}
                alt="main product"
                className="size-full"
                onError={(e) => {
                  e.target.src =
                    "https://via.placeholder.com/300?text=Image+Not+Found";
                }}
              />
            </div>
          </div>
        </div>

        {productData.SubProducts.length > 0 && (
          <div className="flex flex-1 flex-col gap-2 ">
            <h5 className="font-bold mt-4 text-2xl lg:text-3xl">
              {productData.product_name}
            </h5>
            <p className="font-semibold">{productData.product_sub_title}</p>
            <p>{productData.product_description}</p>

            <div className="flex flex-col mt-4 gap-4">
              <div className="flex items-center  text-lg">
                {[...Array(5)].map((_, idx) => (
                  <IoStar
                    key={idx}
                    className={`${
                      idx < productData.average_rating
                        ? "text-yellow-500"
                        : "text-gray-300"
                    }`}
                  />
                ))}
                <p>({productData.average_rating || 0})</p>
              </div>
              <div className="flex items-center gap-2 ml-3 mt-1">
                <TfiCommentAlt /> {productData.total_ratings || 0} comments
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between">
                <p className="text-lg font-medium">Available Sizes </p>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {[
                  ...new Set(productData.SubProducts?.map((sub) => sub.size)),
                ].map((size, index) => (
                  <div
                    key={`size-${index}`}
                    className="flex gap-6 px-3 py-1 cursor-pointer rounded border bg-white text-black border-black"
                  >
                    {size || "N/A"}
                  </div>
                ))}
              </div>
            </div>

            {userType === "vendor" ? (
              <p className="mt-4">
                1 Set ({productData.SubProducts[0]?.no_of_pcs ?? ""} units)
              </p>
            ) : (
              ""
            )}

            {/* <div className="mt-4">
            <p className="text-lg font-medium">Color Available</p>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {productData.SubProducts?.map((subProduct, index) => (
                <div
                  key={`color-${index}`}
                  className="w-6 h-6 rounded-full border-2 border-gray-300"
                  style={{ backgroundColor: subProduct.color_id?.toLowerCase() || 'gray' }}
                  title={subProduct.color_id}
                ></div>
              ))}
            </div>
          </div> */}
            <div className="mt-4">
              <p className="text-lg font-medium">Available Colors</p>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {[
                  ...new Map(
                    productData.SubProducts.map((sub) => [
                      sub.color_shade?.toLowerCase(),
                      sub.shade_img,
                    ])
                  ).entries(),
                ].map(([colorShade, shadeImg], index) => (
                  <img
                    loading="lazy"
                    key={`color-${index}`}
                    src={shadeImg}
                    alt={colorShade}
                    title={colorShade}
                    className="w-8 h-8 rounded-full border-2 border-gray-300 object-cover cursor-pointer"
                  />
                ))}
              </div>
            </div>
            <div className="mt-5 flex flex-col gap-4">
              {isAuthenticated && (
                <div className="">
                  <div className="border border-dark p-2 px-4 text-2xl font-medium rounded-xl w-fit">
                    {userType === "vendor"
                      ? productData.SubProducts[0].vendor_product_price ?? 0
                      : productData.SubProducts[0].customer_product_price ??
                        0}{" "}
                    ₹
                  </div>
                </div>
              )}

              <div className="flex gap-4 items-center">
                <span
                  className={`p-2 rounded-full cursor-pointer transition-colors duration-200 ease-in text-xl ${
                    isWishlisted
                      ? "bg-red-500 text-white"
                      : "bg-tealish text-gray-700 hover:bg-tealish/50"
                  }`}
                  onClick={() => {
                    if (isAuthenticated) {
                      dispatch(toggleWishlistItem(productData.pro_id));
                    } else {
                      navigate("/login");
                    }
                  }}
                  title={
                    isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"
                  }
                >
                  <IoHeartOutline />
                </span>
                <button
                  onClick={handleAddToCartClick}
                  className="flex gap-1 items-center btn theme-btn"
                >
                  <FaCartPlus /> Add To Cart
                </button>
                {/* <button className='flex gap-1 items-center btn theme-outline-btn'>
                Buy Now
            </button> */}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="my-4 mt-5 row ">
        <other.SimilarProducts pro_id={pro_id} />
      </div>

      <other.Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Select Sub Product"
      >
        <div className="flex flex-col gap-4">
          {subProducts.map((sub, idx) => (
            <div
              key={idx}
              className="w-full border rounded-xl p-4 shadow hover:shadow-lg transition flex flex-row items-center gap-4"
            >
              {/* Image on the left */}
              <div className="w-32 aspect-[3/4] overflow-hidden rounded-lg">
                <img
                  loading="lazy"
                  src={sub.sub_pro_img}
                  alt={`sub-product-${idx}`}
                  className="size-full object-cover object-center"
                />
              </div>

              {/* Details on the right */}
              <div className="flex flex-col justify-between flex-grow h-full">
                <div className="space-y-1">
                  <p>
                    <strong>Color:</strong> {sub.color_id}
                  </p>
                  <p>
                    <strong>Size:</strong> {sub.size}
                  </p>
                  {/* <p><strong>Location:</strong> {sub.location}</p> */}
                  {isAuthenticated && (
                    <p>
                      <strong>Price:</strong> ₹
                      {userType === "vendor"
                        ? sub.vendor_product_price
                        : sub.customer_product_price}{" "}
                    </p>
                  )}
                </div>

                {/* Quantity controls */}
                <div className="self-start flex items-center mt-2 gap-2 ">
                  <div className="border border-gray-300 rounded-full overflow-hidden">
                    <button
                      onClick={() => decrementQuantity(sub.sub_pro_id)}
                      className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
                      disabled={quantities[sub.sub_pro_id] <= 1}
                    >
                      -
                    </button>
                    <span className="px-4 py-1 select-none">
                      {quantities[sub.sub_pro_id] || 1}
                    </span>
                    <button
                      onClick={() => incrementQuantity(sub)}
                      className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
                    >
                      +
                    </button>
                  </div>

                  {userType === "vendor" ? (
                    sub.selling_type == "set" ? (
                      <span className="ml-2">
                        {quantities[sub.sub_pro_id] <= 1 ? "Set" : "Sets"}{" "}
                      </span>
                    ) : (
                      <span className="ml-2">
                        {quantities[sub.sub_pro_id] <= 1 ? "Piece" : "Pieces"}
                      </span>
                    )
                  ) : null}
                </div>

                <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                  {/* Note Text */}
                  {userType === "vendor" &&
                  sub.selling_type == "set" &&
                  sub?.no_of_pcs ? (
                    <p className="italic text-sm text-red-600 font-semibold whitespace-nowrap min-w-[180px]">
                      {` ${quantities[sub?.sub_pro_id] || 1} Set = ${
                        sub?.no_of_pcs * (quantities[sub?.sub_pro_id] || 1)
                      } pieces`}{" "}
                      (₹{sub.set_price * (quantities[sub?.sub_pro_id] || 1)})
                    </p>
                  ) : (
                    // Invisible div to take up the same space so button stays right-aligned
                    <div className="min-w-[180px]" />
                  )}

                  {/* Add Button */}
                  {isAuthenticated ? (
                    <div className="flex flex-col gap-1 w-full sm:w-fit">
                      <button
                        className="btn theme-outline-btn disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleAddSubProductToCart(sub)}
                        disabled={sub.no_of_stock <= 0}
                      >
                        Add Item
                      </button>

                      {sub.no_of_stock <= 0 && (
                        <p className="text-red-600 text-sm font-semibold">
                          Out of Stock
                        </p>
                      )}
                    </div>
                  ) : (
                    navigate("/login")
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </other.Modal>
    </div>
  );
};

export default ProductDetail;
